import React from 'react';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AddchartIcon from '@mui/icons-material/Addchart';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, ADDITIONAL_REC_BLUE, BEST_PRACTICE_PINK } from './qcTheme';

const HeaderBlock = styled('div')({
  padding: '24px',
  borderBottom: '1px solid rgba(0,0,0,0.18)',
});

const CrumbRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '12px',
  flexWrap: 'wrap',
});

const Crumb = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'rgba(0,0,0,0.55)',
  padding: '3px 9px 3px 7px',
  marginLeft: '-7px',
  borderRadius: '6px',
  '& .MuiSvgIcon-root': {
    fontSize: '13px',
    color: 'rgba(0,0,0,0.3)',
  },
});

const FilterPill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  marginLeft: 'auto',
  background: '#eef4fc',
  border: '1px solid rgba(25,118,210,0.25)',
  height: '26px',
  padding: '0 6px 0 11px',
  borderRadius: '13px',
  whiteSpace: 'nowrap',
});

const FilterPillIcon = styled(FilterListIcon)({
  fontSize: '15px',
  color: ACCENT_BLUE,
});

const FilterPillLabel = styled('span')({
  fontSize: '0.72rem',
  fontWeight: 600,
  color: ACCENT_BLUE_DARK,
});

const JumpToFirst = styled('button')({
  border: 'none',
  background: 'none',
  padding: 0,
  fontFamily: 'inherit',
  fontSize: '0.7rem',
  fontWeight: 600,
  color: ACCENT_BLUE,
  cursor: 'pointer',
});

const ClearFilter = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  background: 'none',
  padding: '2px',
  borderRadius: '50%',
  cursor: 'pointer',
  color: 'rgba(0,0,0,0.45)',
  '&:hover': {
    background: 'rgba(0,0,0,0.08)',
    color: 'rgba(0,0,0,0.7)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
});

const titleIcon = {
  fontSize: '17px',
  verticalAlign: '-3px',
  marginRight: '7px',
};

const Rosette = styled(WorkspacePremiumIcon)({
  ...titleIcon,
  color: BEST_PRACTICE_PINK,
});

const ArChart = styled(AddchartIcon)({
  ...titleIcon,
  color: ADDITIONAL_REC_BLUE,
});

const QuestionText = styled('h2')({
  margin: 0,
  fontSize: '1.2rem',
  fontWeight: 700,
  lineHeight: 1.35,
  color: 'rgba(0,0,0,0.87)',
  textWrap: 'pretty',
});

export interface QuestionHeaderRowsProps {
  domainTitle: string;
  topicTitle: string;
  bpLabel: string;
  questionTitle: string;
  isBestPractice: boolean;
  filterNames: string[];
  currentInFilteredSet: boolean;
  onJumpToFirst: () => void;
  onClearFilters: () => void;
}

export default function QuestionHeaderRows(props: QuestionHeaderRowsProps): JSX.Element {
  const {
    domainTitle, topicTitle, bpLabel, questionTitle, isBestPractice,
    filterNames, currentInFilteredSet,
    onJumpToFirst, onClearFilters,
  } = props;
  const filterActive = filterNames.length > 0;
  const filterLabel = filterNames.length > 2 ? `${filterNames.length} filters` : filterNames.join(', ');

  return (
    <HeaderBlock>
      <CrumbRow>
        <Crumb>
          {domainTitle}
          <ChevronRightIcon />
          {topicTitle}
          <ChevronRightIcon />
          {bpLabel}
        </Crumb>
        {filterActive && (
          <FilterPill>
            <FilterPillIcon />
            <Tooltip title={filterNames.join(', ')}>
              <FilterPillLabel>{filterLabel}</FilterPillLabel>
            </Tooltip>
            {!currentInFilteredSet && (
              <JumpToFirst type='button' onClick={onJumpToFirst}>Jump to first</JumpToFirst>
            )}
            <ClearFilter type='button' aria-label='Clear filters' onClick={onClearFilters}>
              <CloseIcon />
            </ClearFilter>
          </FilterPill>
        )}
      </CrumbRow>
      <QuestionText>
        {isBestPractice ? (
          <Tooltip title='Best Practice question'>
            <Rosette />
          </Tooltip>
        ) : (
          <Tooltip title='Additional Recommendation question'>
            <ArChart />
          </Tooltip>
        )}
        {questionTitle}
      </QuestionText>
    </HeaderBlock>
  );
}
